// src/pages/WorkInstructions.tsx
// Work Instructions module — SOP library + job-specific work packages
// Briefing: TrafficProcure Work Instructions Module — June 2026

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  MOCK_SOPS, MOCK_JOB_PACKAGES, DISCIPLINE_META, WI_STATUS_META,
  WorkInstruction, WIDiscipline, WIStatus,
  getStepCompletionCount, getHoldPoints, getTotalPhotoRequirements,
} from '../utils/mockDataWorkInstructions'
import {
  MOCK_CLIENT_SPECS, TRADE_RATES, TRADE_RATE_MAP, SOURCE_META, SPEC_STATUS_META,
  ClientSpec, SpecSource, SpecStatus, TradeCode, calcWILabourCost, getLinkedSpecs,
} from '../utils/mockDataClientSpecs'
import { MOCK_JOBS } from '../utils/mockData'
import { MOCK_TASKS } from '../utils/mockDataDrawings'
import { getBOMLinesByDrawingTask, BOM_COMPLIANCE_META } from '../utils/canonicalBOM'
import { traceTaskPipeline } from '../utils/workflowPipeline'
import { recordWIStepSignOff, getWISignOffs, isAllStepsSigned, getIFCWIDrafts, activateIFCWIDraft, checkStepPTWRequirement } from '../utils/mockDataProjectGaps'
import { recordWICompletionForJob } from '../utils/workflowEvents'
import { MOCK_PERMITS } from '../utils/mockDataWHS'
import {
  BookOpen, Plus, Search, Filter, ChevronRight, ChevronDown, X,
  AlertTriangle, CheckCircle, Camera, ClipboardList, Shield,
  Send, Clock, User, FileText, Zap, Lock, RotateCcw,
  Wrench, Award, Package, Eye, ArrowRight, ListChecks,
  DollarSign, Upload, Link, ExternalLink, Library, RefreshCw,
  Calculator, TrendingUp, Download, Globe, PenLine, Link2,
} from 'lucide-react'

// ── Small shared components ───────────────────────────────────────

function DisciplineBadge({ d }: { d: WIDiscipline }) {
  const m = DISCIPLINE_META[d]
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium ${m.bg} ${m.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  )
}

function WIStatusBadge({ status }: { status: WIStatus }) {
  const m = WI_STATUS_META[status]
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${m.bg} ${m.text}`}>
      {m.label}
    </span>
  )
}

function HoldPointPill() {
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-semibold">
      <Lock className="w-2.5 h-2.5" />HOLD
    </span>
  )
}

function ProgressBar({ done, total }: { done: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100)
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-green-500' : 'bg-[#2E75B6]'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-gray-500 tabular-nums">{done}/{total}</span>
    </div>
  )
}

// ── Labour cost panel ─────────────────────────────────────────────

function LabourCostPanel({ wi }: { wi: WorkInstruction }) {
  const steps = wi.steps as (typeof wi.steps[0] & { estimatedHours?: number; tradeCode?: TradeCode })[]
  const labour = calcWILabourCost(steps)
  const linkedSpecs = getLinkedSpecs(wi.id)

  if (labour.totalHours === 0 && linkedSpecs.length === 0) return null

  return (
    <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden">
      {/* Labour cost */}
      {labour.totalHours > 0 && (
        <div className="bg-[#1F3864] px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Calculator className="w-3.5 h-3.5 text-blue-300" />
              <span className="text-xs font-semibold text-white">Labour estimate</span>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-white">
                ${labour.totalCost.toLocaleString('en-AU', { minimumFractionDigits: 0 })}
              </div>
              <div className="text-[10px] text-blue-300">{labour.totalHours.toFixed(1)} hrs total</div>
            </div>
          </div>
          <div className="space-y-1">
            {Object.entries(labour.byTrade).map(([code, { hours, cost }]) => {
              const rate = TRADE_RATE_MAP[code as TradeCode]
              return rate ? (
                <div key={code} className="flex items-center justify-between text-xs">
                  <span className="text-blue-200">{rate.label}</span>
                  <span className="text-white font-mono">{hours.toFixed(1)} hr × ${rate.hourlyRateLoaded} = <span className="font-semibold">${cost.toLocaleString('en-AU')}</span></span>
                </div>
              ) : null
            })}
          </div>
        </div>
      )}
      {/* Linked client specs */}
      {linkedSpecs.length > 0 && (
        <div className="px-4 py-3 space-y-1.5">
          <div className="flex items-center gap-1.5 mb-2">
            <Library className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Client specifications</span>
          </div>
          {linkedSpecs.map(spec => {
            const sm = SOURCE_META[spec.source]
            const clauses = wi.specReferences?.find(r => r.specId === spec.id)?.clauses ?? []
            return (
              <div key={spec.id} className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
                <span className="text-xs font-semibold text-gray-900">{spec.reference}</span>
                {clauses.length > 0 && <span className="text-xs text-gray-500">{clauses.join(', ')}</span>}
                <span className="text-xs text-gray-400 flex-1 truncate">{spec.title}</span>
                {spec.sourceUrl && (
                  <a href={spec.sourceUrl} target="_blank" rel="noreferrer" className="flex-shrink-0">
                    <ExternalLink className="w-3 h-3 text-blue-500 hover:text-blue-700" />
                  </a>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── PM-06: Step Sign-Off Modal ──────────────────────────────────
function StepSignOffModal({
  step,
  wiId,
  signatoryName,
  onConfirm,
  onClose,
}: {
  step: { number: number; title: string }
  wiId: string
  signatoryName: string
  onConfirm: (stepNumber: number, name: string, note: string) => void
  onClose: () => void
}) {
  const [name, setName] = useState(signatoryName)
  const [confirmed, setConfirmed] = useState(false)
  const [note, setNote] = useState('')
  const now = new Date().toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-blue-50">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <PenLine className="w-4 h-4 text-blue-600" />Sign off step
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">Step {step.number} — {step.title}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-700 leading-relaxed">
            <p className="font-medium mb-1">Declaration:</p>
            <p>I confirm that <strong>Step {step.number}: {step.title}</strong> has been completed in full accordance with this work instruction, the applicable SWMS, and all site safety requirements.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Full name <span className="text-red-500">*</span></label>
            <input className="input text-sm" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Site note (optional)</label>
            <input className="input text-sm" value={note} onChange={e => setNote(e.target.value)} placeholder="Any observations, variations, or conditions…" />
          </div>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} className="mt-0.5 w-4 h-4 accent-blue-600 flex-shrink-0" />
            <span className="text-xs text-gray-700">
              I confirm this step is complete and it is safe to proceed to the next step.<br />
              <strong className="text-gray-900">Time recorded: {now}</strong>
            </span>
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button
              onClick={() => name && confirmed && onConfirm(step.number, name, note)}
              disabled={!name || !confirmed}
              className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50">
              <PenLine className="w-3.5 h-3.5" />Sign off step
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Detail panel — full WI view ───────────────────────────────────

function WIDetailPanel({ wi, onClose, canApprove, canDeploy, canSignOff = false }: {
  wi: WorkInstruction
  onClose: () => void
  canApprove: boolean
  canDeploy: boolean
  canSignOff?: boolean
}) {
  const [expandedStep, setExpandedStep] = useState<number | null>(null)
  // PM-06: sign-off state
  const [signOffs, setSignOffs] = useState<Record<number, { name: string; at: string; note: string }>>(() => {
    // Pre-populate from persisted sign-offs if available
    const existing = getWISignOffs(wi.id)
    return Object.fromEntries(existing.map(s => [s.stepNumber, { name: s.signedByName, at: s.signedAt, note: s.note ?? '' }]))
  })
  const [signOffTarget, setSignOffTarget] = useState<{ number: number; title: string } | null>(null)
  const { done, total } = getStepCompletionCount(wi)
  const holdPoints = getHoldPoints(wi)
  const totalPhotos = getTotalPhotoRequirements(wi)
  const steps = wi.steps as (typeof wi.steps[0] & { estimatedHours?: number; tradeCode?: TradeCode })[]
  const totalHours = steps.reduce((s, st) => s + (st.estimatedHours ?? 0), 0)
  // PM-09: job context for PTW badge lookup
  const wiJobId = wi.applicableJobIds?.[0] ?? null

  return (
    <div className="flex flex-col h-full">
      {/* Panel header */}
      <div className="flex items-start justify-between gap-3 px-6 py-4 border-b bg-gray-50">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs font-mono text-gray-400">{wi.reference}</span>
            <span className="text-xs text-gray-400">v{wi.version}</span>
            <WIStatusBadge status={wi.status} />
          </div>
          <h2 className="text-base font-semibold text-gray-900 leading-snug">{wi.title}</h2>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <DisciplineBadge d={wi.discipline} />
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />{wi.estimatedDuration}
            </span>
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <ListChecks className="w-3 h-3" />{wi.steps.length} steps
            </span>
            {totalHours > 0 && (
              <span className="text-xs text-[#2E75B6] flex items-center gap-1 font-medium">
                <Clock className="w-3 h-3" />{totalHours.toFixed(1)} hrs est.
              </span>
            )}
            {holdPoints.length > 0 && (
              <span className="text-xs text-orange-600 font-medium flex items-center gap-1">
                <Lock className="w-3 h-3" />{holdPoints.length} hold point{holdPoints.length !== 1 ? 's' : ''}
              </span>
            )}
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Camera className="w-3 h-3" />{totalPhotos} photos required
            </span>
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors flex-shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Job package banner */}
        {wi.type === 'JOB_PACKAGE' && (
          <div className="mx-6 mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-1.5">
            <div className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-2">Job package details</div>
            {wi.drawingTaskId && (() => {
              const pipeline = traceTaskPipeline(wi.drawingTaskId)
              const bomLines = getBOMLinesByDrawingTask(wi.drawingTaskId)
              const compIssues = bomLines.filter(l => !l.complianceVerifiedForThisJob || l.complianceFlag !== 'COMPLIANT')
              return (
                <div className="mt-1 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-purple-800 mb-1">
                    <Link2 className="w-3 h-3" /> Pipeline: {pipeline.drawingTask?.taskNumber} — {pipeline.drawingTask?.title}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-purple-700 flex-wrap">
                    <span className={`px-1.5 py-0.5 rounded-full font-medium ${pipeline.materialsReady.ready ? 'bg-green-100 text-green-700' : pipeline.materialsReady.blockedByCompliance ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {pipeline.materialsReady.ready ? '✓ Materials ready' : pipeline.materialsReady.blockedByCompliance ? '⚠ Compliance issue' : '○ Materials not ready'}
                    </span>
                    <span>{bomLines.length} BOM line{bomLines.length !== 1 ? 's' : ''}</span>
                    {compIssues.length > 0 && <span className="text-red-600 font-medium">{compIssues.length} compliance issue{compIssues.length !== 1 ? 's' : ''}</span>}
                    {pipeline.configuredTasks.length > 0 && <span>{pipeline.configuredTasks.length} mobile task{pipeline.configuredTasks.length !== 1 ? 's' : ''} configured</span>}
                  </div>
                </div>
              )
            })()}
            {!wi.drawingTaskId && wi.taskReference && (
              <div className="text-xs text-blue-900"><span className="text-blue-500 font-medium">Task ref: </span>{wi.taskReference} <span className="text-amber-500">(free text — not yet linked)</span></div>
            )}
            {wi.drawingReference && <div className="text-xs text-blue-900"><span className="text-blue-500 font-medium">Drawing: </span>{wi.drawingReference}</div>}
            {wi.specificationClause && <div className="text-xs text-blue-900"><span className="text-blue-500 font-medium">Spec clause: </span>{wi.specificationClause}</div>}
            {wi.swmsReference && <div className="text-xs text-blue-900"><span className="text-blue-500 font-medium">SWMS ref: </span>{wi.swmsReference}</div>}
            {wi.assignedToName && (
              <div className="text-xs text-blue-900 flex items-center gap-1 mt-1">
                <User className="w-3 h-3 text-blue-500" />
                <span className="font-medium">Assigned: </span>{wi.assignedToName}
              </div>
            )}
          </div>
        )}

        {/* Site-specific notes */}
        {wi.siteSpecificNotes && (
          <div className="mx-6 mt-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
              <span className="text-xs font-semibold text-amber-800">Site-specific notes</span>
            </div>
            <p className="text-xs text-amber-900 leading-relaxed">{wi.siteSpecificNotes}</p>
          </div>
        )}

        <div className="px-6 py-4 space-y-4">
          {/* Metadata grid */}
          <div className="grid grid-cols-1 gap-3">
            {/* Regulations */}
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <FileText className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Applicable regulations</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {wi.applicableRegulations.map((r, i) => (
                  <span key={i} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{r}</span>
                ))}
              </div>
            </div>

            {/* Credentials */}
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Award className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Required credentials</span>
              </div>
              <ul className="space-y-0.5">
                {wi.requiredCredentials.map((c, i) => (
                  <li key={i} className="text-xs text-gray-700 flex items-center gap-1.5">
                    <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />{c}
                  </li>
                ))}
              </ul>
            </div>

            {/* Tools + PPE side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Wrench className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Tools & equipment</span>
                </div>
                <ul className="space-y-0.5">
                  {wi.requiredTools.map((t, i) => (
                    <li key={i} className="text-xs text-gray-600">· {t}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Shield className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">PPE required</span>
                </div>
                <ul className="space-y-0.5">
                  {wi.ppeRequirements.map((p, i) => (
                    <li key={i} className="text-xs text-gray-600">· {p}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Progress bar — job packages only */}
          {wi.type === 'JOB_PACKAGE' && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-700">Step completion</span>
                <span className="text-xs text-gray-500">{done} of {total} steps</span>
              </div>
              <ProgressBar done={done} total={total} />
            </div>
          )}

          {/* Steps */}
          <div>
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
              Step-by-step procedure
            </div>
            <div className="space-y-2">
              {wi.steps.map((step) => {
                const isOpen = expandedStep === step.number
                const isComplete = !!step.completedAt
                const isHold = step.isHoldPoint

                return (
                  <div
                    key={step.number}
                    className={`rounded-xl border transition-all ${
                      isHold
                        ? 'border-orange-200 bg-orange-50'
                        : isComplete
                        ? 'border-green-200 bg-green-50'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <button
                      className="w-full flex items-center gap-3 px-4 py-3 text-left"
                      onClick={() => setExpandedStep(isOpen ? null : step.number)}
                    >
                      {/* Step number / status icon */}
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                        isHold
                          ? 'bg-orange-500 text-white'
                          : isComplete
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {isHold ? <Lock className="w-3 h-3" /> : isComplete ? <CheckCircle className="w-3 h-3" /> : step.number}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-sm font-medium ${isHold ? 'text-orange-900' : isComplete ? 'text-green-900' : 'text-gray-800'}`}>
                            {step.title}
                          </span>
                          {isHold && <HoldPointPill />}
                          {(step as any).estimatedHours > 0 && !isHold && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[#1F3864]/10 text-[#1F3864] font-semibold">
                              <Clock className="w-2.5 h-2.5" />{(step as any).estimatedHours}h {(step as any).tradeCode ?? ''}
                            </span>
                          )}
                          {step.requiresPhoto && (
                            <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-sky-100 text-sky-700">
                              <Camera className="w-2.5 h-2.5" />{step.photoCount ?? 1}
                            </span>
                          )}
                          {step.qualityCheck && (
                            <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                              <CheckCircle className="w-2.5 h-2.5" />QC
                            </span>
                          )}
                          {step.asBuiltField && (
                            <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-teal-100 text-teal-700">
                              <ClipboardList className="w-2.5 h-2.5" />Record
                            </span>
                          )}
                          {/* PM-09: PTW requirement badge — keyword-driven live permit check */}
                          {wiJobId && (() => {
                            const ptw = checkStepPTWRequirement(step.title, (step as any).instruction ?? '')
                            if (!ptw) return null
                            const activePermit = (MOCK_PERMITS as any[]).find(p =>
                              p.jobId === wiJobId && p.type === ptw.ptwType &&
                              ['APPROVED', 'IN_PROGRESS'].includes(p.status)
                            )
                            const pendingPermit = (MOCK_PERMITS as any[]).find(p =>
                              p.jobId === wiJobId && p.type === ptw.ptwType && p.status === 'SUBMITTED'
                            )
                            if (activePermit) return (
                              <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                                {ptw.icon} PTW {activePermit.permitNumber} active
                              </span>
                            )
                            if (pendingPermit) return (
                              <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                                ⧗ {ptw.label} pending approval
                              </span>
                            )
                            return (
                              <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                                ⚠ {ptw.label} required
                              </span>
                            )
                          })()}
                        </div>
                        {isComplete && (
                          <div className="text-xs text-green-700 mt-0.5">
                            Completed {new Date(step.completedAt!).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      </div>

                      <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isOpen && (
                      <div className="px-4 pb-4 pt-0 space-y-3">
                        <div className="h-px bg-gray-100" />
                        <p className="text-sm text-gray-700 leading-relaxed">{step.instruction}</p>

                        {step.isHoldPoint && step.holdPointRef && (
                          <div className="bg-orange-100 border border-orange-200 rounded-lg px-3 py-2">
                            <div className="text-xs font-semibold text-orange-800 mb-0.5">{step.holdPointRef}</div>
                            <div className="text-xs text-orange-700">{step.holdPointDescription}</div>
                          </div>
                        )}

                        {step.photoRequirement && (
                          <div className="bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <Camera className="w-3 h-3 text-sky-600" />
                              <span className="text-xs font-semibold text-sky-800">{step.photoCount ?? 1} photo{step.photoCount !== 1 ? 's' : ''} required</span>
                            </div>
                            <div className="text-xs text-sky-700">{step.photoRequirement}</div>
                          </div>
                        )}

                        {step.qualityCheck && (
                          <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <CheckCircle className="w-3 h-3 text-purple-600" />
                              <span className="text-xs font-semibold text-purple-800">Quality check</span>
                            </div>
                            <div className="text-xs text-purple-700">{step.qualityCheck}</div>
                          </div>
                        )}

                        {step.asBuiltField && (
                          <div className="bg-teal-50 border border-teal-200 rounded-lg px-3 py-2">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <ClipboardList className="w-3 h-3 text-teal-600" />
                              <span className="text-xs font-semibold text-teal-800">As-built record</span>
                            </div>
                            <div className="text-xs text-teal-700 font-mono">{step.asBuiltField}</div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* PM-06: Field sign-off row */}
                    {canSignOff && !isHold && !signOffs[step.number] && (
                      <div className="px-4 pb-3">
                        <button
                          onClick={() => setSignOffTarget({ number: step.number, title: step.title })}
                          className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5">
                          <PenLine className="w-3 h-3" />Sign off step {step.number}
                        </button>
                      </div>
                    )}
                    {canSignOff && signOffs[step.number] && (
                      <div className="px-4 pb-3 flex items-center gap-2 text-xs text-green-700 bg-green-50 mx-4 mb-3 rounded-lg py-2">
                        <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                        <span>
                          Signed — <strong>{signOffs[step.number].name}</strong> ·{' '}
                          {new Date(signOffs[step.number].at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {signOffs[step.number].note && <span className="text-green-600 italic">· {signOffs[step.number].note}</span>}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* PM-06: All steps signed — work_instruction_completed event */}
          {canSignOff && wi.steps.length > 0 && Object.keys(signOffs).length >= wi.steps.filter(s => !s.isHoldPoint).length && wi.steps.filter(s => !s.isHoldPoint).length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-1.5">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm font-semibold text-green-800">All steps signed off — work instruction completed</span>
              </div>
              <p className="text-xs text-green-700">
                Event <code className="bg-green-100 px-1 rounded">work_instruction_completed</code> triggered ·
                A09 QA Specialist notified · ITP hold points flagged for PM review
              </p>
            </div>
          )}

          {/* Authorisation */}
          {wi.authorisedByName && (
            <div className="text-xs text-gray-500 border-t pt-3 flex items-center gap-1">
              <CheckCircle className="w-3 h-3 text-green-500" />
              Approved by {wi.authorisedByName}
              {wi.authorisedAt && ` — ${new Date(wi.authorisedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`}
            </div>
          )}

          {/* Labour cost + linked specs */}
          <LabourCostPanel wi={wi} />
        </div>
      </div>

      {/* Action footer */}
      {(canApprove || canDeploy) && (
        <div className="border-t bg-white px-6 py-4">
          <div className="flex items-center gap-2 flex-wrap">
            {wi.status === 'PENDING_REVIEW' && canApprove && (
              <>
                <button className="btn-primary flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4" />Approve
                </button>
                <button className="btn-secondary flex items-center gap-2 text-sm">
                  <RotateCcw className="w-4 h-4" />Return for revision
                </button>
              </>
            )}
            {wi.status === 'APPROVED' && canDeploy && wi.type === 'JOB_PACKAGE' && (
              <button className="btn-primary flex items-center gap-2 text-sm">
                <Send className="w-4 h-4" />Deploy to crew
              </button>
            )}
            {wi.status === 'SUBMITTED' && canApprove && (
              <>
                <button className="btn-primary flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4" />Accept & sign off
                </button>
                <button className="btn-secondary flex items-center gap-2 text-sm text-red-600">
                  <RotateCcw className="w-4 h-4" />Flag for rework
                </button>
              </>
            )}
            {wi.status === 'DRAFT' && (
              <button className="btn-secondary flex items-center gap-2 text-sm">
                <ArrowRight className="w-4 h-4" />Submit for review
              </button>
            )}
          </div>
        </div>
      )}

      {/* PM-06: Step sign-off modal */}
      {signOffTarget && (
        <StepSignOffModal
          step={signOffTarget}
          wiId={wi.id}
          signatoryName=""
          onConfirm={(stepNumber, name, note) => {
            const at = new Date().toISOString()
            const newSignOffs = { ...signOffs, [stepNumber]: { name, at, note } }
            setSignOffs(newSignOffs)
            recordWIStepSignOff({
              wiId: wi.id,
              stepNumber,
              stepTitle: signOffTarget.title,
              signedByName: name,
              signedByTier: 'D_FIELD',
              signedAt: at,
              note: note || undefined,
            })
            setSignOffTarget(null)
            // P3-7: If all non-hold-point steps are now signed, flag ITP witness points as ready
            const nonHoldSteps = wi.steps.filter((s: any) => !s.isHoldPoint)
            if (nonHoldSteps.length > 0 && Object.keys(newSignOffs).length >= nonHoldSteps.length) {
              recordWICompletionForJob((wi as any).jobId ?? '')
            }
          }}
          onClose={() => setSignOffTarget(null)}
        />
      )}
    </div>
  )
}

// ── Client Specifications tab ─────────────────────────────────────

function ClientSpecsTab({ canManage }: { canManage: boolean }) {
  const [search, setSearch]   = useState('')
  const [source, setSource]   = useState<SpecSource | 'ALL'>('ALL')
  const [status, setStatus]   = useState<SpecStatus | 'ALL'>('ALL')
  const [selected, setSelected] = useState<ClientSpec | null>(null)
  const [showImport, setShowImport] = useState(false)
  const [importStep, setImportStep] = useState(1)
  const [importMethod, setImportMethod] = useState<'url' | 'pdf' | 'manual'>('url')
  const [importUrl, setImportUrl] = useState('')
  const [importRef, setImportRef] = useState('')
  const [importTitle, setImportTitle] = useState('')

  const filtered = MOCK_CLIENT_SPECS.filter(s => {
    if (source !== 'ALL' && s.source !== source) return false
    if (status !== 'ALL' && s.status !== status) return false
    if (search && !s.reference.toLowerCase().includes(search.toLowerCase()) &&
        !s.title.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  const updateCount = MOCK_CLIENT_SPECS.filter(s => s.updateAvailableFlag).length

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total specs', value: MOCK_CLIENT_SPECS.length, color: 'text-gray-900' },
          { label: 'Current', value: MOCK_CLIENT_SPECS.filter(s=>s.status==='CURRENT').length, color: 'text-green-700' },
          { label: 'TMR MRTS', value: MOCK_CLIENT_SPECS.filter(s=>s.source==='TMR').length, color: 'text-blue-700' },
          { label: 'Update available', value: updateCount, color: updateCount > 0 ? 'text-amber-600' : 'text-gray-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-xs text-gray-500">{label}</div>
          </div>
        ))}
      </div>

      {/* Search + filter */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input className="input pl-9 text-sm" placeholder="Search specs by reference or title…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input text-sm w-auto" value={source} onChange={e => setSource(e.target.value as any)}>
          <option value="ALL">All sources</option>
          {(Object.keys(SOURCE_META) as SpecSource[]).map(s => (
            <option key={s} value={s}>{SOURCE_META[s].label}</option>
          ))}
        </select>
        <select className="input text-sm w-auto" value={status} onChange={e => setStatus(e.target.value as any)}>
          <option value="ALL">All statuses</option>
          <option value="CURRENT">Current</option>
          <option value="SUPERSEDED">Superseded</option>
          <option value="DRAFT">Draft</option>
        </select>
        {canManage && (
          <button onClick={() => { setShowImport(true); setImportStep(1) }} className="btn-primary flex items-center gap-2 text-sm ml-auto">
            <Upload className="w-4 h-4" />Import spec
          </button>
        )}
      </div>

      {/* Import dialog */}
      {showImport && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-blue-900">Import client specification</h4>
              <div className="flex gap-2 mt-1">
                {['url','pdf','manual'].map(m => (
                  <button key={m} onClick={() => setImportMethod(m as any)}
                    className={`text-xs px-2.5 py-1 rounded-lg border font-medium transition-colors ${importMethod === m ? 'bg-[#1F3864] text-white border-[#1F3864]' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                    {m === 'url' ? '🔗 URL link' : m === 'pdf' ? '📄 PDF upload' : '✏️ Manual entry'}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={() => setShowImport(false)}><X className="w-4 h-4 text-gray-400" /></button>
          </div>

          {importMethod === 'url' && (
            <div className="space-y-3">
              <div className="bg-blue-100/60 rounded-lg px-3 py-2 text-xs text-blue-800">
                <Globe className="w-3 h-3 inline mr-1" />
                Enter the URL of the published specification. For TMR MRTS documents, use the TMR website URL. The platform will store the link and check for version updates.
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Source URL <span className="text-red-500">*</span></label>
                <input className="input text-sm" placeholder="https://www.tmr.qld.gov.au/..." value={importUrl} onChange={e => setImportUrl(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Spec reference <span className="text-red-500">*</span></label>
                  <input className="input text-sm" placeholder="e.g. MRTS93" value={importRef} onChange={e => setImportRef(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Version / date</label>
                  <input className="input text-sm" placeholder="e.g. July 2024" />
                </div>
              </div>
            </div>
          )}

          {importMethod === 'pdf' && (
            <div className="space-y-3">
              <div className="border-2 border-dashed border-blue-300 bg-white rounded-xl p-8 text-center">
                <Upload className="w-8 h-8 text-blue-300 mx-auto mb-2" />
                <div className="text-sm text-gray-600 font-medium">Drop PDF or Word file here</div>
                <div className="text-xs text-gray-400 mt-1">Supports PDF and .docx · Max 50 MB</div>
                <button className="mt-3 btn-secondary text-xs">Browse files</button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Spec reference <span className="text-red-500">*</span></label>
                  <input className="input text-sm" placeholder="e.g. MRTS93" value={importRef} onChange={e => setImportRef(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Source</label>
                  <select className="input text-sm">
                    {(Object.keys(SOURCE_META) as SpecSource[]).map(s => (
                      <option key={s} value={s}>{SOURCE_META[s].label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {importMethod === 'manual' && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                Use manual entry for restricted specs (e.g. MRTS245) or where no document is available for upload.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Spec reference <span className="text-red-500">*</span></label>
                  <input className="input text-sm" placeholder="e.g. MRTS245" value={importRef} onChange={e => setImportRef(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Source</label>
                  <select className="input text-sm">
                    {(Object.keys(SOURCE_META) as SpecSource[]).map(s => (
                      <option key={s} value={s}>{SOURCE_META[s].label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Full title <span className="text-red-500">*</span></label>
                  <input className="input text-sm" placeholder="e.g. ITS Telecommunications Network" value={importTitle} onChange={e => setImportTitle(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Version / date</label>
                  <input className="input text-sm" placeholder="e.g. July 2020" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Discipline</label>
                  <select className="input text-sm">
                    {(Object.keys(DISCIPLINE_META) as WIDiscipline[]).map(d => (
                      <option key={d} value={d}>{DISCIPLINE_META[d].label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Key requirements (one per line)</label>
                  <textarea className="input text-sm resize-none" rows={4} placeholder="Enter the main installation methodology requirements this spec prescribes…" />
                </div>
              </div>
            </div>
          )}

          {/* Link to SOPs */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Link to existing SOPs (optional)</label>
            <div className="flex flex-wrap gap-1.5">
              {MOCK_SOPS.filter(s => s.status === 'APPROVED').slice(0, 6).map(sop => (
                <label key={sop.id} className="flex items-center gap-1.5 text-xs bg-white border border-gray-200 px-2 py-1 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" className="rounded" />
                  <span className="font-mono text-gray-600">{sop.reference}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={() => setShowImport(false)} className="btn-secondary text-sm">Cancel</button>
            <button disabled={!importRef} className="btn-primary text-sm flex items-center gap-2 disabled:opacity-40">
              <Library className="w-4 h-4" />Add to spec library
            </button>
          </div>
        </div>
      )}

      {/* Spec list + detail panel */}
      <div className="flex gap-4 items-start">
        <div className="flex-1 min-w-0 space-y-2">
          {filtered.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl flex flex-col items-center py-16 text-center">
              <Library className="w-10 h-10 text-gray-300 mb-3" />
              <div className="font-medium text-gray-500">No specs match your filters</div>
            </div>
          ) : filtered.map(spec => {
            const sm = SOURCE_META[spec.source]
            const st = SPEC_STATUS_META[spec.status]
            const isSelected = selected?.id === spec.id

            return (
              <div key={spec.id} onClick={() => setSelected(isSelected ? null : spec)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${isSelected ? 'border-blue-400 ring-2 ring-blue-100 shadow-sm' : spec.updateAvailableFlag ? 'border-amber-300' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${sm.bg} ${sm.text} flex items-center gap-1`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sm.dot}`} />
                        {sm.label}
                      </span>
                      <span className="text-sm font-bold text-gray-900">{spec.reference}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${st.bg} ${st.text}`}>{st.label}</span>
                      {spec.updateAvailableFlag && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1 font-medium">
                          <RefreshCw className="w-2.5 h-2.5" />Update available
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-800 font-medium">{spec.title}</div>
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      <DisciplineBadge d={spec.discipline} />
                      <span className="text-xs text-gray-400">{spec.version}</span>
                      {spec.linkedSopIds.length > 0 && (
                        <span className="text-xs text-blue-600 flex items-center gap-1">
                          <BookOpen className="w-3 h-3" />{spec.linkedSopIds.length} SOP{spec.linkedSopIds.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      {spec.hasUploadedFile && (
                        <span className="text-xs text-teal-600 flex items-center gap-1">
                          <FileText className="w-3 h-3" />PDF uploaded
                        </span>
                      )}
                      {spec.sourceUrl && !spec.hasUploadedFile && (
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Link className="w-3 h-3" />URL link
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 ${isSelected ? 'text-blue-500' : 'text-gray-400'}`} />
                </div>
              </div>
            )
          })}
        </div>

        {/* Spec detail panel */}
        {selected && (
          <div className="w-[480px] flex-shrink-0 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm sticky top-6" style={{ maxHeight: 'calc(100vh - 160px)' }}>
            <div className="flex flex-col h-full">
              <div className="flex items-start justify-between gap-3 px-5 py-4 border-b bg-gray-50">
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 ${SOURCE_META[selected.source].bg} ${SOURCE_META[selected.source].text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${SOURCE_META[selected.source].dot}`} />
                      {SOURCE_META[selected.source].label}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${SPEC_STATUS_META[selected.status].bg} ${SPEC_STATUS_META[selected.status].text}`}>
                      {SPEC_STATUS_META[selected.status].label}
                    </span>
                  </div>
                  <div className="text-base font-bold text-gray-900">{selected.reference}</div>
                  <div className="text-sm text-gray-700">{selected.title}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{selected.version}</div>
                </div>
                <button onClick={() => setSelected(null)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                <DisciplineBadge d={selected.discipline} />

                <p className="text-sm text-gray-700 leading-relaxed">{selected.description}</p>

                {/* Source / file */}
                <div className="flex gap-2 flex-wrap">
                  {selected.sourceUrl && (
                    <a href={selected.sourceUrl} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 border border-blue-100 px-3 py-1.5 rounded-lg">
                      <ExternalLink className="w-3 h-3" />Open on TMR website
                    </a>
                  )}
                  {selected.hasUploadedFile && (
                    <button className="flex items-center gap-1.5 text-xs text-teal-600 bg-teal-50 border border-teal-100 px-3 py-1.5 rounded-lg hover:bg-teal-100">
                      <Download className="w-3 h-3" />Download {selected.uploadedFileName}
                    </button>
                  )}
                </div>

                {/* Key requirements */}
                <div>
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Key methodology requirements</div>
                  <div className="space-y-1.5">
                    {selected.keyRequirements.map((req, i) => (
                      <div key={i} className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-gray-800">
                        <CheckCircle className="w-3 h-3 text-blue-500 flex-shrink-0 mt-0.5" />
                        {req}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Linked SOPs */}
                {selected.linkedSopIds.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Linked SOPs</div>
                    <div className="space-y-1">
                      {selected.linkedSopIds.map(id => {
                        const sop = MOCK_SOPS.find(s => s.id === id)
                        return sop ? (
                          <div key={id} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs">
                            <BookOpen className="w-3 h-3 text-blue-500" />
                            <span className="font-mono text-gray-500">{sop.reference}</span>
                            <span className="text-gray-700 flex-1 truncate">{sop.title}</span>
                            <WIStatusBadge status={sop.status} />
                          </div>
                        ) : null
                      })}
                    </div>
                  </div>
                )}

                {/* Related specs */}
                {selected.relatedSpecIds.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Related specifications</div>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.relatedSpecIds.map(id => {
                        const rel = MOCK_CLIENT_SPECS.find(s => s.id === id)
                        return rel ? (
                          <button key={id} onClick={() => setSelected(rel)}
                            className="text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 px-2.5 py-1 rounded-full font-medium">
                            {rel.reference}
                          </button>
                        ) : null
                      })}
                    </div>
                  </div>
                )}

                {/* Import metadata */}
                <div className="text-xs text-gray-400 border-t pt-3 space-y-0.5">
                  <div>Imported {new Date(selected.importedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })} by {selected.importedByName}</div>
                  {selected.lastReviewedAt && <div>Last reviewed {new Date(selected.lastReviewedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</div>}
                </div>
              </div>

              {canManage && (
                <div className="border-t px-5 py-3 flex gap-2">
                  <button className="btn-secondary flex items-center gap-2 text-xs flex-1">
                    <PenLine className="w-3.5 h-3.5" />Edit spec details
                  </button>
                  <button className="btn-secondary flex items-center gap-2 text-xs flex-1">
                    <BookOpen className="w-3.5 h-3.5" />Create SOP from spec
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── SOP Library tab ───────────────────────────────────────────────

function SopLibraryTab({
  canManage, onSelect, selected,
}: {
  canManage: boolean
  onSelect: (wi: WorkInstruction) => void
  selected: WorkInstruction | null
}) {
  const [search, setSearch]   = useState('')
  const [discipline, setDisc] = useState<WIDiscipline | 'ALL'>('ALL')
  const [status, setStatus]   = useState<WIStatus | 'ALL'>('ALL')
  const [showNewForm, setShowNewForm] = useState(false)

  const filtered = MOCK_SOPS.filter(wi => {
    if (discipline !== 'ALL' && wi.discipline !== discipline) return false
    if (status !== 'ALL' && wi.status !== status) return false
    if (search && !wi.title.toLowerCase().includes(search.toLowerCase()) &&
        !wi.reference.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  const byStatus = (s: WIStatus) => MOCK_SOPS.filter(wi => wi.status === s).length

  return (
    <div className="space-y-4">
      {/* Stats strip */}
      <div className="grid grid-cols-4 gap-3">
        {([
          { label: 'Total SOPs', value: MOCK_SOPS.length, color: 'text-gray-900' },
          { label: 'Approved', value: byStatus('APPROVED'), color: 'text-blue-700' },
          { label: 'Draft', value: byStatus('DRAFT'), color: 'text-amber-600' },
          { label: 'Disciplines', value: new Set(MOCK_SOPS.map(s => s.discipline)).size, color: 'text-purple-700' },
        ] as const).map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-xs text-gray-500">{label}</div>
          </div>
        ))}
      </div>

      {/* Search + filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            className="input pl-9 text-sm"
            placeholder="Search SOPs…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <select
          className="input text-sm w-auto"
          value={discipline}
          onChange={e => setDisc(e.target.value as any)}
        >
          <option value="ALL">All disciplines</option>
          {(Object.keys(DISCIPLINE_META) as WIDiscipline[]).map(d => (
            <option key={d} value={d}>{DISCIPLINE_META[d].label}</option>
          ))}
        </select>

        <select
          className="input text-sm w-auto"
          value={status}
          onChange={e => setStatus(e.target.value as any)}
        >
          <option value="ALL">All statuses</option>
          {(Object.keys(WI_STATUS_META) as WIStatus[]).map(s => (
            <option key={s} value={s}>{WI_STATUS_META[s].label}</option>
          ))}
        </select>

        {canManage && (
          <button
            onClick={() => setShowNewForm(!showNewForm)}
            className="btn-primary flex items-center gap-2 text-sm ml-auto"
          >
            <Plus className="w-4 h-4" />New SOP
          </button>
        )}
      </div>

      {/* New SOP form */}
      {showNewForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-blue-900">New SOP — Library</h4>
            <button onClick={() => setShowNewForm(false)}><X className="w-4 h-4 text-gray-400" /></button>
          </div>
          <div className="bg-blue-100/60 rounded-lg px-3 py-2 text-xs text-blue-800">
            <Zap className="w-3 h-3 inline mr-1" />
            SOPs are published to the library and can be used as the base for any job-specific work package.
            New SOPs require PM approval before becoming Active.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">SOP title <span className="text-red-500">*</span></label>
              <input className="input text-sm" placeholder="e.g. Signal pole foundation — install and backfill" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Discipline <span className="text-red-500">*</span></label>
              <select className="input text-sm">
                {(Object.keys(DISCIPLINE_META) as WIDiscipline[]).map(d => (
                  <option key={d} value={d}>{DISCIPLINE_META[d].label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Estimated duration</label>
              <input className="input text-sm" placeholder="e.g. 1 day / 4–6 hours" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Applicable regulations</label>
              <input className="input text-sm" placeholder="e.g. MRTS50 cl. 6.3.4, AS 3600 (comma-separated)" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowNewForm(false)} className="btn-secondary text-sm">Cancel</button>
            <button className="btn-primary text-sm flex items-center gap-2">
              <ArrowRight className="w-4 h-4" />Save as draft
            </button>
          </div>
        </div>
      )}

      {/* SOP list */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl flex flex-col items-center py-16 text-center">
            <BookOpen className="w-10 h-10 text-gray-300 mb-3" />
            <div className="font-medium text-gray-500">No SOPs match your filters</div>
            <div className="text-sm text-gray-400 mt-1">Try clearing the search or discipline filter</div>
          </div>
        ) : (
          filtered.map(wi => {
            const isSelected = selected?.id === wi.id
            const holdCount = getHoldPoints(wi).length
            const photoCount = getTotalPhotoRequirements(wi)

            return (
              <div
                key={wi.id}
                onClick={() => onSelect(wi)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${isSelected ? 'border-blue-400 ring-2 ring-blue-100 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <div className="flex items-start gap-3">
                  {/* Discipline dot */}
                  <div className={`w-3 h-3 rounded-full mt-1 flex-shrink-0 ${DISCIPLINE_META[wi.discipline].dot}`} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="text-xs font-mono text-gray-400">{wi.reference}</span>
                          <span className="text-xs text-gray-400">v{wi.version}</span>
                        </div>
                        <div className="font-medium text-gray-900 text-sm leading-snug">{wi.title}</div>
                      </div>
                      <div className="flex-shrink-0">
                        <WIStatusBadge status={wi.status} />
                      </div>
                    </div>

                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <DisciplineBadge d={wi.discipline} />
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />{wi.estimatedDuration}
                      </span>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <ListChecks className="w-3 h-3" />{wi.steps.length} steps
                      </span>
                      {holdCount > 0 && (
                        <span className="text-xs text-orange-600 flex items-center gap-1">
                          <Lock className="w-3 h-3" />{holdCount} hold pt
                        </span>
                      )}
                      {photoCount > 0 && (
                        <span className="text-xs text-sky-600 flex items-center gap-1">
                          <Camera className="w-3 h-3" />{photoCount} photos
                        </span>
                      )}
                    </div>

                    <div className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                      {wi.applicableRegulations.slice(0, 2).join(' · ')}
                      {wi.applicableRegulations.length > 2 && ` +${wi.applicableRegulations.length - 2} more`}
                    </div>
                  </div>

                  <ChevronRight className={`w-4 h-4 text-gray-400 flex-shrink-0 mt-1 transition-transform ${isSelected ? 'text-blue-500' : ''}`} />
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ── Job Work Packages tab ─────────────────────────────────────────

function JobPackagesTab({
  scopedJobIds, canCreatePackage, onSelect, selected,
}: {
  scopedJobIds: string[] | null
  canCreatePackage: boolean
  onSelect: (wi: WorkInstruction) => void
  selected: WorkInstruction | null
}) {
  const visibleJobs = scopedJobIds !== null
    ? MOCK_JOBS.filter(j => scopedJobIds.includes(j.id) && !['COMPLETED', 'CLOSED'].includes(j.status))
    : MOCK_JOBS.filter(j => !['COMPLETED', 'CLOSED'].includes(j.status))

  const [activeJob, setActiveJob] = useState(visibleJobs[0]?.id ?? '')
  const [statusFilter, setStatusFilter] = useState<WIStatus | 'ALL'>('ALL')
  const [showNewForm, setShowNewForm] = useState(false)

  const jobPackages = MOCK_JOB_PACKAGES.filter(wi =>
    wi.applicableJobIds?.includes(activeJob) &&
    (statusFilter === 'ALL' || wi.status === statusFilter)
  )

  const statusCounts = (s: WIStatus) =>
    MOCK_JOB_PACKAGES.filter(w => w.applicableJobIds?.includes(activeJob) && w.status === s).length

  return (
  <div className="space-y-4">
  {/* PM-05: IFC-generated WI drafts pending review */}
        {(() => {
          const drafts = getIFCWIDrafts(activeJob).filter(d => d.status === 'DRAFT_REVIEW')
          if (drafts.length === 0) return null
          return (
            <div className="space-y-3">
              {drafts.map(draft => (
                <div key={draft.id} className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-xs font-bold text-indigo-800 bg-indigo-100 px-1.5 py-0.5 rounded">A09</span>
                        <span className="text-xs text-indigo-700 font-medium">QA Specialist — IFC Work Instruction Draft</span>
                        <span className="text-xs bg-white border border-indigo-200 text-indigo-600 px-1.5 py-0.5 rounded-full">{draft.steps.length} steps</span>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">{draft.title}</p>
                      <p className="text-xs text-indigo-700 mt-0.5">{draft.agentNote}</p>
                      <div className="mt-2 space-y-1">
                        {draft.steps.slice(0, 3).map(step => (
                          <div key={step.number} className="flex items-center gap-1.5 text-xs text-gray-600">
                            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0 ${
                              step.isHoldPoint ? 'bg-orange-500' : 'bg-indigo-500'
                            }`}>{step.isHoldPoint ? 'H' : step.number}</span>
                            <span>{step.title}</span>
                          </div>
                        ))}
                        {draft.steps.length > 3 && (
                          <p className="text-xs text-gray-400 ml-6">+ {draft.steps.length - 3} more steps</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => activateIFCWIDraft(draft.id)}
                        className="text-xs bg-indigo-600 text-white hover:bg-indigo-700 px-2.5 py-1.5 rounded-lg font-medium flex items-center gap-1">
                        <CheckCircle className="w-3.5 h-3.5" />Accept
                      </button>
                      <button
                        onClick={() => activateIFCWIDraft(draft.id)}
                        className="text-xs bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 px-2.5 py-1.5 rounded-lg font-medium">
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        })()}

        {/* Job tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <span className="text-xs text-gray-500 flex-shrink-0">Job:</span>
        {visibleJobs.map(j => (
          <button
            key={j.id}
            onClick={() => setActiveJob(j.id)}
            className={`flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${activeJob === j.id ? 'bg-[#1F3864] text-white border-[#1F3864]' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
          >
            <span>{j.jobNumber}</span>
            {MOCK_JOB_PACKAGES.filter(w => w.applicableJobIds?.includes(j.id) && ['PENDING_REVIEW','SUBMITTED','REWORK_REQUIRED'].includes(w.status)).length > 0 && (
              <span className={`text-xs font-bold px-1 rounded ${activeJob === j.id ? 'bg-amber-400 text-white' : 'bg-amber-100 text-amber-700'}`}>
                {MOCK_JOB_PACKAGES.filter(w => w.applicableJobIds?.includes(j.id) && ['PENDING_REVIEW','SUBMITTED','REWORK_REQUIRED'].includes(w.status)).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Status pipeline */}
      <div className="bg-white border border-gray-200 rounded-xl px-5 py-3">
        <div className="flex items-center gap-4 flex-wrap text-sm">
          {([
            ['DRAFT', 'Draft'],
            ['APPROVED', 'Approved'],
            ['DEPLOYED', 'Deployed'],
            ['IN_PROGRESS', 'In progress'],
            ['SUBMITTED', 'Pending PM'],
            ['COMPLETE', 'Complete'],
          ] as [WIStatus, string][]).map(([s, label]) => {
            const count = statusCounts(s)
            const m = WI_STATUS_META[s]
            return (
              <div key={s} className="flex items-center gap-2">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${count > 0 ? `${m.bg} ${m.text}` : 'bg-gray-100 text-gray-400'}`}>
                  {count}
                </span>
                <span className={`text-xs ${count > 0 ? 'text-gray-700' : 'text-gray-400'}`}>{label}</span>
              </div>
            )
          })}
          {canCreatePackage && (
            <button
              onClick={() => setShowNewForm(!showNewForm)}
              className="ml-auto btn-primary flex items-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" />New work package
            </button>
          )}
        </div>
      </div>

      {/* New work package form */}
      {showNewForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-blue-900">New job work package — {visibleJobs.find(j => j.id === activeJob)?.jobNumber}</h4>
            <button onClick={() => setShowNewForm(false)}><X className="w-4 h-4 text-gray-400" /></button>
          </div>
          <div className="bg-blue-100/60 rounded-lg px-3 py-2 text-xs text-blue-800">
            <Zap className="w-3 h-3 inline mr-1" />
            Select a base SOP from the library. The work package will be pre-filled with the SOP steps — you can then add job-specific drawing references, site notes, and photo requirements.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Base SOP <span className="text-red-500">*</span></label>
              <select className="input text-sm">
                <option value="">Select SOP from library…</option>
                {MOCK_SOPS.filter(s => s.status === 'APPROVED').map(s => (
                  <option key={s.id} value={s.id}>{s.reference} — {s.title}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Link to drawing task <span className="text-purple-600 font-semibold">★ Phase 1 — real FK</span>
              </label>
              <select className="input text-sm">
                <option value="">— Pick drawing task (connects this WI to the pipeline) —</option>
                {MOCK_TASKS.filter(t => t.jobId && t.level > 1).map(t => (
                  <option key={t.id} value={t.id}>{t.taskNumber} — {t.title} ({t.discipline})</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">Selecting a task connects this WI to that task's BOM, material readiness, and evidence trail.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Drawing reference</label>
              <input className="input text-sm" placeholder="e.g. J1-C02 Sheet 2 Detail A" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Specification clause</label>
              <input className="input text-sm" placeholder="e.g. MRTS50 cl. 6.3.4" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Site-specific notes</label>
              <textarea className="input text-sm resize-none" rows={3} placeholder="Add any site-specific conditions, RFI references, or hazard notes…" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowNewForm(false)} className="btn-secondary text-sm">Cancel</button>
            <button className="btn-primary text-sm flex items-center gap-2">
              <ArrowRight className="w-4 h-4" />Create work package
            </button>
          </div>
        </div>
      )}

      {/* Status filter */}
      <div className="flex gap-2 flex-wrap">
        {(['ALL', 'DRAFT', 'APPROVED', 'DEPLOYED', 'IN_PROGRESS', 'SUBMITTED', 'REWORK_REQUIRED', 'COMPLETE'] as (WIStatus | 'ALL')[]).map(f => (
          <button
            key={f}
            onClick={() => setStatusFilter(f)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${statusFilter === f ? 'bg-[#1F3864] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {f === 'ALL' ? 'All' : WI_STATUS_META[f].label}
          </button>
        ))}
      </div>

      {/* Work packages list */}
      {jobPackages.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl flex flex-col items-center py-16 text-center">
          <ClipboardList className="w-10 h-10 text-gray-300 mb-3" />
          <div className="font-medium text-gray-500">No work packages for this job yet</div>
          {canCreatePackage && (
            <div className="text-sm text-gray-400 mt-1">Create a work package from an approved SOP above</div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {jobPackages.map(wi => {
            const isSelected = selected?.id === wi.id
            const { done, total } = getStepCompletionCount(wi)
            const holdPoints = getHoldPoints(wi)
            const isUrgent = ['PENDING_REVIEW', 'SUBMITTED', 'REWORK_REQUIRED'].includes(wi.status)

            return (
              <div
                key={wi.id}
                onClick={() => onSelect(wi)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${
                  isSelected ? 'border-blue-400 ring-2 ring-blue-100 shadow-sm' :
                  isUrgent ? 'border-amber-300' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${DISCIPLINE_META[wi.discipline].dot}`} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-xs font-mono text-gray-400 mb-0.5">{wi.reference}</div>
                        <div className="font-medium text-gray-900 text-sm leading-snug">{wi.title}</div>
                      </div>
                      <WIStatusBadge status={wi.status} />
                    </div>

                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <DisciplineBadge d={wi.discipline} />
                      {wi.assignedToName && (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <User className="w-3 h-3" />{wi.assignedToName}
                        </span>
                      )}
                      {!wi.assignedToName && wi.status === 'APPROVED' && (
                        <span className="text-xs text-amber-600 flex items-center gap-1">
                          <User className="w-3 h-3" />Not yet assigned
                        </span>
                      )}
                      {holdPoints.length > 0 && (
                        <span className="text-xs text-orange-600 flex items-center gap-1">
                          <Lock className="w-3 h-3" />{holdPoints.length} hold pt{holdPoints.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    {/* Step progress — show for deployed/in-progress/submitted */}
                    {['DEPLOYED', 'IN_PROGRESS', 'SUBMITTED', 'REWORK_REQUIRED', 'COMPLETE'].includes(wi.status) && (
                      <div className="mt-2">
                        <ProgressBar done={done} total={total} />
                      </div>
                    )}

                    {/* Rework note */}
                    {wi.reworkNote && (
                      <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />{wi.reworkNote}
                      </div>
                    )}
                  </div>

                  <ChevronRight className={`w-4 h-4 text-gray-400 flex-shrink-0 mt-1 ${isSelected ? 'text-blue-500' : ''}`} />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────

export function WorkInstructions() {
  const { isTier, user } = useAuthStore()
  const [tab, setTab] = useState<'sop' | 'packages' | 'specs'>('sop')
  const [selected, setSelected] = useState<WorkInstruction | null>(null)

  const isAdmin    = isTier('A_DEVELOPER')
  const isPM       = isTier('B_PM')
  const isPC       = isTier('B_PC')
  const isField    = user?.tier === 'D_FIELD'

  const canManageSops     = isAdmin || isPM
  const canCreatePackage  = isAdmin || isPM || isPC
  const canApprove        = isAdmin || isPM
  const canDeploy         = isAdmin || isPM || isPC

  // Scope job packages for PC users
  const scopedJobIds = user?.tier === 'B_PC' ? (user.assignedJobs ?? null) : null

  // Field users: read-only view of assigned packages only
  if (isField) {
    const myPackages = MOCK_JOB_PACKAGES.filter(wi =>
      wi.assignedToId && ['DEPLOYED', 'IN_PROGRESS', 'SUBMITTED'].includes(wi.status)
    )
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">My work instructions</h1>
          <p className="text-sm text-gray-500 mt-0.5">Work packages assigned to you for field execution</p>
        </div>
        <div className="space-y-3">
          {myPackages.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl flex flex-col items-center py-16 text-center">
              <ClipboardList className="w-10 h-10 text-gray-300 mb-3" />
              <div className="font-medium text-gray-500">No work instructions assigned</div>
            </div>
          ) : (
            myPackages.map(wi => {
              const { done, total } = getStepCompletionCount(wi)
              return (
                <div
                  key={wi.id}
                  onClick={() => setSelected(wi === selected ? null : wi)}
                  className="bg-white border border-gray-200 rounded-xl p-4 cursor-pointer hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="text-sm font-semibold text-gray-900">{wi.title}</div>
                    <WIStatusBadge status={wi.status} />
                  </div>
                  <DisciplineBadge d={wi.discipline} />
                  <div className="mt-2"><ProgressBar done={done} total={total} /></div>
                </div>
              )
            })
          )}
        </div>
        {selected && (
          <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setSelected(null)}>
            <div className="w-full max-w-2xl bg-white h-full overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
              <WIDetailPanel wi={selected} onClose={() => setSelected(null)} canApprove={false} canDeploy={false} canSignOff={true} />
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#2E75B6]" />
            Work Instructions
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            SOP library and job-specific work packages · step-by-step field procedures with hold points and photo evidence
          </p>
        </div>
        {/* Lifecycle status key */}
        <div className="hidden lg:flex items-center gap-1 text-xs text-gray-400">
          {(['DRAFT','APPROVED','DEPLOYED','IN_PROGRESS','COMPLETE'] as WIStatus[]).map((s, i, arr) => (
            <span key={s} className="flex items-center gap-1">
              <span className={`px-1.5 py-0.5 rounded ${WI_STATUS_META[s].bg} ${WI_STATUS_META[s].text} font-medium`}>
                {WI_STATUS_META[s].label}
              </span>
              {i < arr.length - 1 && <ArrowRight className="w-3 h-3" />}
            </span>
          ))}
        </div>
      </div>

      {/* Two-panel layout when something is selected */}
      <div className={`flex gap-6 ${selected ? 'items-start' : ''}`}>
        {/* Left — main content */}
        <div className={`flex-1 min-w-0 ${selected ? 'max-w-none' : ''}`}>
          {/* Tab strip */}
          <div className="flex border-b border-gray-200 mb-5">
            {([
              { key: 'sop', label: 'SOP Library', count: MOCK_SOPS.length, icon: BookOpen },
              { key: 'packages', label: 'Job Work Packages', count: MOCK_JOB_PACKAGES.length, icon: ClipboardList },
              { key: 'specs', label: 'Client Specs', count: MOCK_CLIENT_SPECS.length, icon: Library },
            ] as const).map(({ key, label, count, icon: Icon }) => (
              <button
                key={key}
                onClick={() => { setTab(key); setSelected(null) }}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  tab === key
                    ? 'border-[#2E75B6] text-[#2E75B6]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${tab === key ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                  {count}
                </span>
              </button>
            ))}
          </div>

          {tab === 'sop' && (
            <SopLibraryTab
              canManage={canManageSops}
              onSelect={wi => setSelected(selected?.id === wi.id ? null : wi)}
              selected={selected}
            />
          )}
          {tab === 'packages' && (
            <JobPackagesTab
              scopedJobIds={scopedJobIds}
              canCreatePackage={canCreatePackage}
              onSelect={wi => setSelected(selected?.id === wi.id ? null : wi)}
              selected={selected}
            />
          )}
          {tab === 'specs' && (
            <ClientSpecsTab canManage={canManageSops} />
          )}
        </div>

        {/* Right — detail panel */}
        {selected && (
          <div className="w-[520px] flex-shrink-0 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm sticky top-6" style={{ maxHeight: 'calc(100vh - 120px)' }}>
            <WIDetailPanel
              wi={selected}
              onClose={() => setSelected(null)}
              canApprove={canApprove}
              canDeploy={canDeploy}
            />
          </div>
        )}
      </div>
    </div>
  )
}
